import { ResourceSample, SERVER_TIMING_METRIC } from '@dto/selftest.dto';

/** Default 250 entries fill up on one dashboard; the export wants the whole session. */
export const RESOURCE_TIMING_BUFFER = 2000;

/** A gap this far below the send interval means the chunk was held back and released with its neighbour. */
export const BUFFERED_GAP_RATIO = 0.25;

export function median(values: number[]): number {
  if (!values.length) return NaN;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/** Arrival-to-arrival differences of a monotonically recorded timeline. */
export function gapsBetween(arrivalsMs: number[]): number[] {
  return arrivalsMs.slice(1).map((t, i) => Math.round(t - arrivalsMs[i]));
}

/** True when most chunks arrived squeezed together instead of one per interval. */
export function isBuffered(gapsMs: number[], intervalMs: number): boolean {
  if (gapsMs.length < 2) return false;
  const squeezed = gapsMs.filter(gap => gap < intervalMs * BUFFERED_GAP_RATIO).length;
  return squeezed > gapsMs.length / 2;
}

/** Rounds the timing fields and drops the origin from the URL, so the export stays readable. */
export function toResourceSample(entry: PerformanceResourceTiming, origin: string): ResourceSample {
  const r = Math.round;
  return {
    name: entry.name.startsWith(origin) ? entry.name.slice(origin.length) : entry.name,
    initiatorType: entry.initiatorType,
    nextHopProtocol: entry.nextHopProtocol,
    startTime: r(entry.startTime),
    fetchStart: r(entry.fetchStart),
    domainLookupStart: r(entry.domainLookupStart),
    connectStart: r(entry.connectStart),
    secureConnectionStart: r(entry.secureConnectionStart),
    connectEnd: r(entry.connectEnd),
    requestStart: r(entry.requestStart),
    responseStart: r(entry.responseStart),
    responseEnd: r(entry.responseEnd),
    duration: r(entry.duration),
    transferSize: entry.transferSize,
    encodedBodySize: entry.encodedBodySize,
    serverMs: entry.serverTiming?.find(t => t.name === SERVER_TIMING_METRIC)?.duration,
  };
}

export function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
